import {HiCheck as CheckIcon, HiMiniXMark} from 'react-icons/hi2'
import {useTranslations} from 'use-intl'
import {PropsWithChildren} from 'react'
import clsx from 'clsx'
import {AppearanceFilter, CategoryFilter, Filter} from '~/gql/graphql'

type FilterOptionProps = {
    isActive: boolean
    onActivate: () => void
    onDeactivate: () => void
}

type CategoryFilterOptionProps = PropsWithChildren<
    FilterOptionProps & {
        category: Filter | CategoryFilter
        isSubFilter?: boolean
        isSubFilterActive?: boolean
    }
>

export const CategoryFilterOption = ({
    category,
    isActive,
    isSubFilter = false,
    isSubFilterActive = false,
    onActivate,
    onDeactivate,
    children,
}: CategoryFilterOptionProps) => {
    const t = useTranslations('productList.filter')
    return (
        <div
            className={clsx(
                'border-l-2 flex justify-between items-center hover:bg-gradient-to-r focus-within:bg-gradient-to-r from-nova/50 to-nova/10',
                isActive ? 'border-nova' : 'border-transparent',
                isActive &&
                    !isSubFilterActive &&
                    'bg-gradient-to-r from-nova/50 to-nova/10'
            )}
        >
            <label aria-label={category.name ?? undefined} className="flex-grow cursor-pointer">
                <div
                    className={clsx(
                        'flex flex-row items-center pr-2 py-2 md:py-1 text-left w-full leading-5',
                        isSubFilter ? 'pl-7' : 'pl-5'
                    )}
                >
                    <input
                        type="checkbox"
                        value={category.id ?? ''}
                        checked={isActive}
                        className="sr-only"
                        onChange={isActive ? onDeactivate : onActivate}
                    />
                    {children}
                </div>
            </label>
            <button
                aria-hidden={!isActive}
                className={clsx('mr-2 shrink-0', !isActive && 'opacity-0')}
                onClick={(event) => {
                    event.stopPropagation()
                    onDeactivate()
                }}
                // @ts-ignore
                inert={!isActive ? 'true' : undefined}
            >
                <HiMiniXMark className="size-5" />
                <span className="sr-only">{t('deselectCategory', {category: category.name})}</span>
            </button>
        </div>
    )
}

type AppearanceFilterOptionProps = FilterOptionProps & {
    appearance: AppearanceFilter
}

export const AppearanceFilterOption = ({
    appearance,
    isActive,
    onActivate,
    onDeactivate,
}: AppearanceFilterOptionProps) => {
    return (
        <label
            className="group cursor-pointer focus-within:ring-2 ring-offset-2 ring-offset-gray-500 ring-nova rounded-full transition duration-200 ease-in-out"
            aria-label={appearance.name!}
            key={appearance.id!}
            title={appearance.name!}
        >
            <input
                type="checkbox"
                name="appearance filter option"
                value={appearance.id!}
                checked={isActive}
                className="sr-only"
                onChange={isActive ? onDeactivate : onActivate}
            />
            <div
                aria-hidden="true"
                className={clsx(
                    'relative size-10 rounded-full border shadow-lg',
                    isActive
                        ? 'border-comet ring-1 ring-comet group-hover:md:border-plasma group-hover:md:ring-plasma'
                        : 'border-white'
                )}
                style={{backgroundColor: appearance.value ?? 'transparent'}}
            >
                {isActive && (
                    <>
                        <div className="absolute -right-1.5 -top-1.5 size-4 rounded-full bg-comet group-hover:md:hidden">
                            <CheckIcon className="size-full p-0.5" />
                        </div>
                        <div className="absolute -right-1.5 -top-1.5 size-4 rounded-full bg-plasma hidden group-hover:md:block">
                            <HiMiniXMark className="size-full p-0.5" />
                        </div>
                    </>
                )}
            </div>
        </label>
    )
}

type SizeFilterOptionProps = FilterOptionProps & {
    size: Filter
}

export const SizeFilterOption = ({
    size,
    isActive,
    onActivate,
    onDeactivate,
}: SizeFilterOptionProps) => {
    return (
        <label
            className="group relative cursor-pointer focus-within:ring-2 ring-offset-2 ring-offset-gray-500 ring-nova rounded transition duration-200 ease-in-out"
            key={size.id!}
        >
            <div
                className={clsx(
                    'min-w-12 rounded border text-sm px-2 py-1 text-center bg-gray-700/10 shadow-lg hover:bg-gray-700/60 transition-colors duration-200 ease-in-out',
                    isActive
                        ? 'border-comet ring-1 ring-comet group-hover:md:border-plasma group-hover:md:ring-plasma'
                        : 'border-white'
                )}
            >
                <input
                    type="checkbox"
                    name="size-choice"
                    value={size?.id!}
                    checked={isActive}
                    className="sr-only"
                    onChange={isActive ? onDeactivate : onActivate}
                />
                <span>{size.name}</span>
            </div>
            {isActive && (
                <>
                    <div className="absolute -right-2 -top-2 size-5 rounded-full bg-comet">
                        <CheckIcon className="size-full p-0.5" />
                    </div>
                    <div className="absolute -right-2 -top-2 size-5 rounded-full bg-plasma hidden group-hover:md:block">
                        <HiMiniXMark className="size-full p-0.5" />
                    </div>
                </>
            )}
        </label>
    )
}
